import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/auth-shell";
import { AuthLegalLinks } from "@/components/auth/auth-legal-links";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import type { ForgotPasswordRequest } from "@/lib/api-types";
import { authErrorMessage } from "@/lib/auth-errors";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");

  const mut = useMutation({
    mutationFn: (body: ForgotPasswordRequest) =>
      api.post<{ ok: true }>("/auth/forgot-password", body),
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email || mut.isPending) return;
    mut.mutate({ email });
  }

  const err = authErrorMessage(mut.error);

  if (mut.isSuccess) {
    return (
      <AuthShell footer={<AuthLegalLinks />}>
        <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-brand" strokeWidth={1.5} />
          <h1 className="mt-4 font-display text-3xl font-black tracking-tight">
            Check your email
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            If an account exists for <span className="text-foreground">{email}</span>,
            we've sent instructions to reset your password.
          </p>
          <Button asChild variant="outline" className="mt-6">
            <Link to="/login">
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell footer={<AuthLegalLinks />}>
      <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8">
        <h1 className="font-display text-3xl font-black tracking-tight">
          Reset your password
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Enter the email you signed up with. We'll send you a reset link.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="fp-email">Email</Label>
            <Input
              id="fp-email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={mut.isPending}
            />
          </div>

          {err && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={!email || mut.isPending}
          >
            {mut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              "Send reset link"
            )}
          </Button>
        </form>
      </div>

      <div className="mt-6 text-center text-sm">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}
